import BaseInput from 'components/inputs/BaseInput';
import { SelectOption } from 'components/inputs/BaseSelect';
import BaseTextarea from 'components/inputs/BaseTextarea';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import LoadingOverlay from 'components/Overlays/LoadingOverlay';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlaySection from 'components/Overlays/OverlaySection';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import React, { useState } from 'react';
import { AddSubmittal } from 'records/interactions/AddSubmittal';
import { NewSubmittalData } from 'records/NewSubmittal';
import {
  SubmittalCategory,
  submittalCategoryListToSelectOptions,
} from 'records/SubmittalCategory';
import { SubmittalStatus } from 'records/SubmittalStatus';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';
import SpecInfoFields from '../SpecInfoFields';

interface Props extends WithAppStoreProps {
  interaction: AddSubmittal;
}

const AddSubmittalOverlay = (props: Props) => {
  const [creatingSubmittal, setCreatingSubmittal] = useState<boolean>(false);

  const { translate } = useTranslator(['submittalsPage', 'buttons']);

  const handleSubmittalChange = (
    field: keyof NewSubmittalData,
    val: string | number | SubmittalCategory | SubmittalStatus
  ): void => {
    const { interaction, actions } = props;

    const updatedInteraction = interaction.setIn(['submittal', field], val);

    actions.updateInteraction(updatedInteraction);
  };

  const handleCancelClick = (): void => {
    props.actions.clearInteractions();
  };

  const createDisabled = (): boolean => {
    const { interaction } = props;
    const { specsectionnumber, description, responsiblecompany } =
      interaction.submittal;

    return (
      specsectionnumber.length === 0 ||
      description.length === 0 ||
      responsiblecompany.length === 0
      // duefromsub === 0
    );
  };

  const handleCreateSubmittal = async (): Promise<void> => {
    const { interaction, actions } = props;

    try {
      setCreatingSubmittal(true);

      await actions.createSubmittal(interaction.submittal, interaction.files);
      await actions.querySubmittals();
    } catch (err) {
      console.log('unable to create submittal');
      toast.error(
        translate('toastText:submittalCreateError.message'),
        translate('toastText:submittalCreateError.title')
      );
    } finally {
      actions.clearInteractions();
    }
  };

  const { interaction, appState } = props;
  const { submittalCategories } = appState;
  const { submittal } = interaction;

  const translatedCategories = submittalCategories.map((category) => {
    const categoryType: `${string}:${string}` =
      `submittalsPage:categories.${category.kind}` as `${string}:${string}`;

    return new SubmittalCategory({
      kind: category.kind,
      display: translate(categoryType),
    });
  });

  return (
    <OverlayStdContainer
      id="add-submittal-overlay"
      style={{ backgroundColor: 'var(--white-2)' }}
    >
      {creatingSubmittal ? (
        <LoadingOverlay text={translate('submittalsPage:creatingSubmittal')} />
      ) : null}
      <OverlayHeader
        id="add-submittal-overlay-header"
        title={translate('submittalsPage:newSubmittal')}
        onCancelClick={handleCancelClick}
      />
      <div className="column justify-between oa grow-1">
        <div className="my2 mx3">
          <OverlaySection label={translate('submittalsPage:generalDetails')}>
            <SpecInfoFields
              id="add-submittal-overlay-spec"
              defaultSpecNumber={submittal.specsectionnumber}
              onSpecNumberChange={(val): void =>
                handleSubmittalChange('specsectionnumber', val)
              }
            />
            <div className="mb3">
              <Label label={translate('submittalsPage:submittalCategory')} />
              <SingleSelect
                id="add-submittal-overlay-category"
                placeholder={translate('submittalsPage:placeholders.select')}
                value={translatedCategories.reduce<SelectOption | null>(
                  (acc, sc) => {
                    if (sc.kind !== submittal.category) {
                      return acc;
                    }

                    return sc.toSelectOption();
                  },
                  null
                )}
                options={submittalCategoryListToSelectOptions(
                  translatedCategories
                )}
                onChange={(sc: SelectOption): void => {
                  handleSubmittalChange('category', sc.value);
                }}
              />
            </div>
            <div className="mb3">
              <Label label={translate('submittalsPage:description')} required />
              <BaseTextarea
                id="add-submittal-overlay-description"
                defaultValue={submittal.description}
                className="w100"
                onChange={({
                  target: { value },
                }: React.ChangeEvent<HTMLTextAreaElement>): void => {
                  handleSubmittalChange('description', value);
                }}
              />
            </div>
            <div className="mb3">
              <Label
                label={translate('submittalsPage:responsibleCompany')}
                required
              />
              <BaseInput
                id="add-submittal-overlay-company"
                onChange={({
                  target: { value },
                }: React.ChangeEvent<HTMLInputElement>): void => {
                  handleSubmittalChange('responsiblecompany', value);
                }}
              />
            </div>
            <div className="mb3">
              <Label label={translate('submittalsPage:dueSubmitter')} />
              <DateDropdown
                id="add-submittal-overlay-duefromsub"
                date={submittal.duefromsub > 0 ? submittal.duefromsub : null}
                onChange={(date: Date): void => {
                  handleSubmittalChange(
                    'duefromsub',
                    date ? date.getTime() / 1000 : 0
                  );
                }}
              />
            </div>
            <div className="mb3">
              <Label label={translate('submittalsPage:dueReviewer')} />
              <DateDropdown
                id="add-submittal-overlay-duefromreviewer"
                date={
                  submittal.duefromreviewer > 0
                    ? submittal.duefromreviewer
                    : null
                }
                onChange={(date: Date): void => {
                  handleSubmittalChange(
                    'duefromreviewer',
                    date ? date.getTime() / 1000 : 0
                  );
                }}
              />
            </div>
            <div className="row">
              <div className="w50 mr3">
                <Label label={translate('submittalsPage:leadTime')} />
                <BaseInput
                  id="add-submittal-overlay-leadtime"
                  onChange={({
                    target: { value },
                  }: React.ChangeEvent<HTMLInputElement>): void => {
                    handleSubmittalChange('leadtime', value);
                  }}
                />
              </div>
              <div className="w50">
                <Label label={translate('submittalsPage:requiredOnSite')} />
                <DateDropdown
                  id="add-submittal-overlay-requiredonsite"
                  date={
                    submittal.requiredonsite > 0
                      ? submittal.requiredonsite
                      : null
                  }
                  onChange={(date: Date): void => {
                    handleSubmittalChange(
                      'requiredonsite',
                      date ? date.getTime() / 1000 : 0
                    );
                  }}
                />
              </div>
            </div>
          </OverlaySection>
        </div>
      </div>
      <OverlayFooter
        id="add-submittal-overlay-footer"
        confirmText={translate('buttons:save')}
        onCancelClick={handleCancelClick}
        onConfirmClick={handleCreateSubmittal}
        confirmDisabled={createDisabled()}
      />
    </OverlayStdContainer>
  );
};

export default withAppStore<Props>(AddSubmittalOverlay);
